import { LISTING_REFETCH_INTERVAL, isolatedPairBlacklist } from '@/config/consts'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { WHYPE_ADDRESS_HYPEREVM } from '@/domain/euler/nativeHype'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfoQuery } from '@/domain/market-info/useMarketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useMarketWalletInfoQuery } from '@/domain/wallet/useMarketWalletInfo'
import { useELend } from '@/features/elend/logic/useELend'
import { useIsolatedPairs } from '@/features/isolated/logic/useIsolatedPairs'
import { aggregateStats as aggregatePooledStats } from '@/features/markets/logic/aggregate-stats'
import { useEVaults } from '@/features/vaults/logic/useEVaults'
import { bigNumberify } from '@/utils/bigNumber'
import { type ReactNode, createContext, useContext, useMemo } from 'react'
import { formatUnits } from 'viem'
import { useAccount, useBalance } from 'wagmi'
import type { YieldOpportunity, YieldSource } from '../types'
import {
  mapELendVaultsToYieldOpportunities,
  mapEVaultsToYieldOpportunities,
  mapIsolatedPairsToYieldOpportunities,
  mapPooledReservesToYieldOpportunities,
} from '../utils'
import type { StrategyUtilizationMap } from '../utils/mapEVaultToYieldOpportunity'
import { usePooledAverageApy } from './usePooledAverageApy'

export interface AggregateMarketStats {
  totalSuppliedUSD: NormalizedUnitNumber
  totalBorrowedUSD: NormalizedUnitNumber
  totalAvailableUSD: NormalizedUnitNumber
}

export interface UseUnifiedEarnOpportunitiesResult {
  opportunities: YieldOpportunity[]
  isLoading: boolean
  errors: Partial<Record<YieldSource, Error | null>>
  stats: {
    totalCount: number
    bySource: Record<YieldSource, number>
  }
  aggregateMarketStats: AggregateMarketStats
}

export interface UseUnifiedEarnOpportunitiesOptions {
  /**
   * When true, the underlying ELend fetch walks every user subaccount to
   * surface positions held outside the main account. Disabled by default
   * because listing/overview surfaces only render market-level stats and
   * don't benefit from the extra N-vaults × ~16-subaccounts multicall.
   *
   * Pass `true` from user-facing position views (e.g. the Assets tab) where
   * subaccount balances must be visible.
   */
  includeSubAccountPositions?: boolean
}

/**
 * Returns the unified earn opportunities result by running all the underlying
 * queries. Call this directly when you only need the data in one place in a
 * subtree. When multiple consumers in the same subtree need it, prefer
 * wrapping with `UnifiedEarnOpportunitiesProvider` + `useSharedUnifiedEarnOpportunities`
 * so the aggregation (and its dependent memos) runs once.
 */
export function useUnifiedEarnOpportunities(
  options: UseUnifiedEarnOpportunitiesOptions = {},
): UseUnifiedEarnOpportunitiesResult {
  const { includeSubAccountPositions = false } = options
  const { address: userAddress } = useAccount()
  const { chainId } = usePageChainId()

  const { data: nativeHypeBalance } = useBalance({
    address: userAddress,
    chainId,
    query: {
      enabled: !!userAddress && chainId === E_LEND_CHAIN_ID,
      refetchInterval: LISTING_REFETCH_INTERVAL,
    },
  })

  const { vaults: evaults, isLoading: isLoadingEVaults, error: evaultError } = useEVaults(userAddress)

  const {
    vaults: elendVaults,
    isLoading: isLoadingELend,
    error: elendError,
  } = useELend(userAddress, { includeSubAccountPositions })

  const {
    pairs: isolatedPairs,
    marketStats: isolatedMarketStats,
    userDataMap: isolatedUserDataMap,
    isLoading: isLoadingIsolated,
  } = useIsolatedPairs()

  const { marketInfo, isPending: isLoadingPooled } = useMarketInfoQuery({
    chainId,
    refetchInterval: LISTING_REFETCH_INTERVAL,
  })
  const { data: pooledAverageApyByAsset } = usePooledAverageApy(chainId)

  const walletInfo = useMarketWalletInfoQuery({ chainId, refetchInterval: LISTING_REFETCH_INTERVAL })

  const strategyUtilizationMap = useMemo((): StrategyUtilizationMap => {
    const map = new Map<string, number>()
    for (const vault of elendVaults) {
      if (vault.utilization !== undefined) {
        map.set(vault.address.toLowerCase(), vault.utilization)
      }
    }
    return map
  }, [elendVaults])

  // Build intrinsic APY lookup by underlying asset address from elend vaults
  const intrinsicApyByAsset = useMemo(() => {
    const map = new Map<string, number>()
    for (const vault of elendVaults) {
      if (vault.intrinsicAPY !== undefined && vault.intrinsicAPY > 0 && vault.underlyingAsset) {
        const assetKey = vault.underlyingAsset.toLowerCase()
        // Keep highest intrinsic APY if same asset appears multiple times
        const existing = map.get(assetKey)
        if (existing === undefined || vault.intrinsicAPY > existing) {
          map.set(assetKey, vault.intrinsicAPY)
        }
      }
    }
    return map
  }, [elendVaults])

  const pooledUserDataMap = useMemo(() => {
    if (!marketInfo?.reserves) return undefined

    const map = new Map<
      string,
      {
        walletBalance: NormalizedUnitNumber
        userDeposit: NormalizedUnitNumber
        userDepositUsd: NormalizedUnitNumber
      }
    >()

    for (const reserve of marketInfo.reserves) {
      const walletBalance = walletInfo.findWalletBalanceForToken(reserve.token)
      const userDeposit = reserve.aTokenBalance
      const userDepositUsd = NormalizedUnitNumber(userDeposit.multipliedBy(reserve.priceInUSD))

      map.set(reserve.token.address.toLowerCase(), {
        walletBalance,
        userDeposit,
        userDepositUsd,
      })
    }

    return map
  }, [marketInfo, walletInfo])

  const opportunities = useMemo(() => {
    const evaultOpps = mapEVaultsToYieldOpportunities(evaults, strategyUtilizationMap)
    const elendOpps = mapELendVaultsToYieldOpportunities(elendVaults)
    const filteredIsolatedPairs = isolatedPairs.filter(
      (pair) => !isolatedPairBlacklist.includes(pair.address.toLowerCase()),
    )
    const isolatedOpps = mapIsolatedPairsToYieldOpportunities(filteredIsolatedPairs, chainId, isolatedUserDataMap)

    const pooledOpps = marketInfo?.reserves
      ? mapPooledReservesToYieldOpportunities(
          marketInfo.reserves.filter((r) => r.supplyAvailabilityStatus !== 'no' || r.status === 'frozen'),
          chainId,
          pooledUserDataMap,
          intrinsicApyByAsset,
          pooledAverageApyByAsset,
        )
      : []

    const allOpps = [...evaultOpps, ...elendOpps, ...isolatedOpps, ...pooledOpps]

    if (!nativeHypeBalance || chainId !== E_LEND_CHAIN_ID) return allOpps

    const nativeHype = nativeHypeBalance.value
    if (nativeHype === 0n) return allOpps

    return allOpps.map((opp) => {
      if (opp.source !== 'pooled' && opp.source !== 'isolated') return opp

      const tokenAddr = opp.token?.address?.toLowerCase()
      if (tokenAddr !== WHYPE_ADDRESS_HYPEREVM.toLowerCase()) return opp

      const nativeFormatted = formatUnits(nativeHype, nativeHypeBalance.decimals)
      const nativeAmount = Number(nativeFormatted)
      const unitPrice = opp.token ? Number(opp.token.unitPriceUsd) : 0
      const nativeUsd = nativeAmount * unitPrice

      const existingBalance = opp.walletBalance ?? NormalizedUnitNumber(0)
      const wrappedAmount = existingBalance.toNumber()
      const wrappedUsd = wrappedAmount * unitPrice
      const combinedBalance = NormalizedUnitNumber(existingBalance.plus(nativeFormatted))

      return {
        ...opp,
        walletBalance: combinedBalance,
        walletBreakdown: {
          nativeUsd,
          wrappedUsd,
          nativeAmount,
          wrappedAmount,
        },
      }
    })
  }, [
    evaults,
    elendVaults,
    isolatedPairs,
    marketInfo,
    chainId,
    strategyUtilizationMap,
    pooledUserDataMap,
    isolatedUserDataMap,
    intrinsicApyByAsset,
    pooledAverageApyByAsset,
    nativeHypeBalance,
  ])

  const stats = useMemo(() => {
    const bySource: Record<YieldSource, number> = {
      evault: 0,
      elend: 0,
      isolated: 0,
      pooled: 0,
    }

    for (const opp of opportunities) {
      bySource[opp.source]++
    }

    return {
      totalCount: opportunities.length,
      bySource,
    }
  }, [opportunities])

  const aggregateMarketStats = useMemo((): AggregateMarketStats => {
    const pooledStats = marketInfo ? aggregatePooledStats(marketInfo) : null

    let totalSupplied = bigNumberify(0)
    let totalBorrowed = bigNumberify(0)

    if (pooledStats) {
      totalSupplied = totalSupplied.plus(pooledStats.totalMarketSizeUSD)
      totalBorrowed = totalBorrowed.plus(pooledStats.totalBorrowsUSD)
    }

    if (isolatedMarketStats) {
      totalSupplied = totalSupplied.plus(isolatedMarketStats.totalMarketSizeUSD)
      totalBorrowed = totalBorrowed.plus(isolatedMarketStats.totalBorrowsUSD)
    }

    for (const vault of elendVaults) {
      if (vault.tvlUsd) {
        totalSupplied = totalSupplied.plus(vault.tvlUsd)
      }
      if (vault.totalBorrowed) {
        totalBorrowed = totalBorrowed.plus(vault.totalBorrowed)
      }
    }

    const totalAvailable = totalSupplied.minus(totalBorrowed)

    return {
      totalSuppliedUSD: NormalizedUnitNumber(totalSupplied),
      totalBorrowedUSD: NormalizedUnitNumber(totalBorrowed),
      totalAvailableUSD: NormalizedUnitNumber(totalAvailable.isNegative() ? 0 : totalAvailable),
    }
  }, [marketInfo, isolatedMarketStats, elendVaults])

  const isLoading = isLoadingEVaults || isLoadingELend || isLoadingIsolated || isLoadingPooled || walletInfo.isLoading

  const errors: Partial<Record<YieldSource, Error | null>> = {
    evault: evaultError,
    elend: elendError,
  }

  return {
    opportunities,
    isLoading,
    errors,
    stats,
    aggregateMarketStats,
  }
}

/**
 * Context that memoizes the result of the (expensive) unified earn aggregation
 * across a subtree so multiple consumers (overview stats, markets table,
 * featured section, etc.) share the same computed value instead of each
 * mounting their own observer chain.
 */
const UnifiedEarnOpportunitiesContext = createContext<UseUnifiedEarnOpportunitiesResult | null>(null)

function UnifiedEarnOpportunitiesBridge({
  children,
  includeSubAccountPositions,
}: {
  children: ReactNode
  includeSubAccountPositions: boolean
}) {
  const value = useUnifiedEarnOpportunities({ includeSubAccountPositions })
  return <UnifiedEarnOpportunitiesContext.Provider value={value}>{children}</UnifiedEarnOpportunitiesContext.Provider>
}

export interface UnifiedEarnOpportunitiesProviderProps {
  children: ReactNode
  /**
   * Forwarded to `useUnifiedEarnOpportunities`. Set true on surfaces that
   * render user positions (Assets tab); leave false on market-overview
   * surfaces to skip the N-vaults × ~16-subaccounts multicall.
   */
  includeSubAccountPositions?: boolean
}

/**
 * Mount once at the top of a page that renders multiple consumers of the
 * unified earn data via `useSharedUnifiedEarnOpportunities`. No-op if nested
 * inside another provider.
 */
export function UnifiedEarnOpportunitiesProvider({
  children,
  includeSubAccountPositions = false,
}: UnifiedEarnOpportunitiesProviderProps) {
  const existing = useContext(UnifiedEarnOpportunitiesContext)
  if (existing !== null) return <>{children}</>
  return (
    <UnifiedEarnOpportunitiesBridge includeSubAccountPositions={includeSubAccountPositions}>
      {children}
    </UnifiedEarnOpportunitiesBridge>
  )
}

/**
 * Reads the unified earn aggregation from the nearest
 * `UnifiedEarnOpportunitiesProvider`. Use this for sibling consumers inside a
 * wrapped page so they share one observer chain instead of each re-running the
 * full aggregation.
 *
 * Throws if there is no provider above — callers outside a provider should use
 * `useUnifiedEarnOpportunities` instead.
 */
export function useSharedUnifiedEarnOpportunities(): UseUnifiedEarnOpportunitiesResult {
  const ctx = useContext(UnifiedEarnOpportunitiesContext)
  if (ctx === null) {
    throw new Error('useSharedUnifiedEarnOpportunities must be used inside a <UnifiedEarnOpportunitiesProvider>')
  }
  return ctx
}
